import React from 'react'
import { Link } from 'react-router'

import CompanyLogoOrNameTableCell from '@/components/misc/CompanyLogoOrNameTableCell'
import CopyButton from '@/components/misc/CopyButton'
import ExternalLink from '@/components/misc/ExternalLink'
import CopyLink from '@/components/misc/PanelInviteEmailTemplate/CopyLink'
import PersistentOverlayTrigger from '@/components/misc/PersistentOverlayTrigger'
import ConfigureEmailsButton from '@/components/misc/ReferralLinksTable/components/ConfigureEmails/ConfigureEmailsButton'
import DeleteLinkButton from '@/components/misc/ReferralLinksTable/components/DeleteLinkButton'
import DuplicateButton from '@/components/misc/ReferralLinksTable/components/DuplicateButton'
import IntroduceTo from '@/components/misc/ReferralLinksTable/components/IntroduceTo'
import ToggleIsCompassLinkButton from '@/components/misc/ReferralLinksTable/components/ToggleIsCompassLinkButton'
import { isReferralLinkCollectionItem } from '@/components/misc/ReferralLinksTable/helpers'
import { useIsViewingAsFromCompany } from '@/components/misc/ReferralLinksTable/hooks/useIsViewingAsFromCompany'
import { shareableLinkRowId } from '@/components/misc/ReferralLinksTable/lib/helpers'
import { ReferralLinkTableRow } from '@/components/misc/ReferralLinksTable/types'
import { Button } from '@/components/ui/button'
import { TableCell, TableRow } from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import { REFERRAL_LINK_TYPE, REFERRALS_TAB } from '@/lib/constants'
import { buildDebugTooltip, truncateText } from '@/lib/helpers/helperFunctions'
import { ROUTES } from '@/lib/routes'
interface Props {
  referralLink: ReferralLinkTableRow
  onEditClick: (referralLink: Props['referralLink']) => void
}

const LINK_CODE_TRUNCATE_THRESHOLD = 100

const ReferralLinksTableRow: React.FC<Props> = ({
  referralLink,
  onEditClick,
}) => {
  const currentCompany = useCurrentCompanyOrFail()
  const isViewingAsFromCompany = useIsViewingAsFromCompany()
  const numReferrals = referralLink.numReferrals

  const otherCompany = isViewingAsFromCompany
    ? referralLink.toCompany
    : referralLink.fromCompany

  const referralLinkPath = ROUTES.referralLink({
    params: {
      referralLinkId: referralLink.id.toString(),
      referralLinkCode: referralLink.currentCompanyCode,
    },
  })

  const referralLinkUrl = window.location.origin + referralLinkPath
  const linkCode = referralLink.currentCompanyCode

  return (
    <TableRow
      id={shareableLinkRowId(referralLink.id)}
      key={referralLink['@id']}
      title={buildDebugTooltip({
        ID: referralLink.id,
        createdAt: referralLink.createdAt,
        currentCompanyCode: referralLink.currentCompanyCode,
        wasDuplicated: referralLink.wasDuplicated,
      })}
    >
      {/* Introduce to / Partner company */}
      <TableCell>
        {isReferralLinkCollectionItem(referralLink) ? (
          <IntroduceTo referralLink={referralLink} />
        ) : (
          <CompanyLogoOrNameTableCell company={otherCompany} />
        )}
      </TableCell>

      {/* Referrals booked */}
      <TableCell className="text-center">
        {numReferrals && numReferrals > 0 ? (
          <Link
            className="py-2 underline"
            to={ROUTES.referrals({
              searchParams: {
                tab: isViewingAsFromCompany
                  ? REFERRALS_TAB.sent
                  : REFERRALS_TAB.received,
                referralLinkCode: referralLink.currentCompanyCode,
              },
            })}
          >
            {numReferrals}
          </Link>
        ) : (
          <span className="inline-block py-2">{numReferrals}</span>
        )}
      </TableCell>

      {/* Link code */}
      <TableCell>
        <div className="flex flex-col gap-x-2 gap-y-1">
          <PersistentOverlayTrigger
            id={`referralLink-code-${referralLink.currentCompanyCode}`}
            tooltip={
              linkCode.length > LINK_CODE_TRUNCATE_THRESHOLD ? linkCode : null
            }
          >
            <div className="flex flex-wrap items-center gap-x-1">
              {truncateText(linkCode, {
                maxLength: LINK_CODE_TRUNCATE_THRESHOLD,
              })}

              {referralLink.type === REFERRAL_LINK_TYPE.COMPASS && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="text-dim text-xs"
                      style={{ borderBottom: '1px dotted grey' }}
                    >
                      (Compass link)
                    </div>
                  </TooltipTrigger>

                  <TooltipContent>
                    Any referrals sent via Compass will be associated with this
                    link
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </PersistentOverlayTrigger>

          <div className="flex flex-wrap gap-2">
            <CopyButton textToCopy={linkCode}>
              {({ copyText, justCopied }) =>
                justCopied ? (
                  <span className="text-success">Code copied!</span>
                ) : (
                  <Button
                    variant="plain"
                    className="hover:underline"
                    onClick={copyText}
                  >
                    Copy
                  </Button>
                )
              }
            </CopyButton>

            <div>|</div>

            <Button
              variant="plain"
              className="hover:underline"
              onClick={() => onEditClick(referralLink)}
            >
              Edit
            </Button>

            {currentCompany['@id'] === referralLink.toCompany['@id'] && (
              <>
                {referralLink.canMarkAsCompassLink && (
                  <>
                    <div>|</div>

                    <ToggleIsCompassLinkButton
                      referralLink={referralLink}
                      isCompassLinkValue={true}
                    />
                  </>
                )}

                {referralLink.isCompassLink && (
                  <>
                    <div>|</div>

                    <ToggleIsCompassLinkButton
                      referralLink={referralLink}
                      isCompassLinkValue={false}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </TableCell>

      {/* Link actions */}
      <TableCell>
        <div className="flex flex-col gap-2">
          <Button asChild variant="outline-secondary" size="sm">
            <ExternalLink iconPlacement="start" href={referralLinkUrl}>
              Preview
            </ExternalLink>
          </Button>

          <CopyLink
            textToCopy={referralLinkUrl}
            buttonText="Copy link"
            variant="outline-primary"
            size="sm"
            className="w-full sm:w-auto"
            style={{ minWidth: '95px' }}
          />

          {referralLink.isRoundRobinLink && (
            <DuplicateButton referralLinkId={referralLink.id} />
          )}

          {referralLink.canBeDeleted && (
            <DeleteLinkButton referralLinkId={referralLink.id} />
          )}

          {isViewingAsFromCompany && (
            <ConfigureEmailsButton referralLink={referralLink} />
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}

export default ReferralLinksTableRow
